import React from 'react';
import { withRouter } from 'react-router-dom';
import CONSTANT from '../../constant';
import Loader from '../../component/common/Loader';
import {
    getEmployeeAuthorityDetail,
    getKpiReport,
    updatePeriodStatus,
} from '../../actions/AppraisalCycleAction';
import { formatDateDDMMYY } from '../../utils';

const TYPE_LABELS: any = {
    hr:        { label: 'Employee KPI Report',      icon: 'fa-id-card-o',      color: 'text-info'    },
    reporting: { label: 'Reporting Manager',        icon: 'fa-user',           color: 'text-primary' },
    reviewing: { label: 'Reviewing Manager',        icon: 'fa-users',          color: 'text-warning' },
    accepting: { label: 'Accepting Authority',      icon: 'fa-check-square-o', color: 'text-success' },
    apar:      { label: 'APAR Report',              icon: 'fa-file-text',      color: 'text-danger'  },
};

const ROMAN_LABELS = ['i)','ii)','iii)','iv)','v)','vi)','vii)','viii)','ix)','x)'];

const DEFAULT_TASKS = [
    { id: 1, particular: 'i)',    taskDescription: '', weightage: '', deliverables: '', achievement: '' },
    { id: 2, particular: 'ii)',   taskDescription: '', weightage: '', deliverables: '', achievement: '' },
    { id: 3, particular: 'iii)',  taskDescription: '', weightage: '', deliverables: '', achievement: '' },
    { id: 4, particular: 'iv)',   taskDescription: '', weightage: '', deliverables: '', achievement: '' },
    { id: 5, particular: 'v)',    taskDescription: '', weightage: '', deliverables: '', achievement: '' },
];

class HrReportDetailList extends React.Component<any, any> {
    constructor(props: any) {
        super(props);
        this.state = {
            showLoader: false,
            employeeCode: '',
            type: '',
            detail: null,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        const params       = new URLSearchParams(this.props.location.search);
        const type         = params.get('type') || 'self';
        const employeeCode = params.get('employeeCode') || '';

        document.title = `${TYPE_LABELS[type]?.label || 'Report'} - ${CONSTANT.organisation.name}`;
        this.setState({ type, employeeCode, showLoader: true });

        getEmployeeAuthorityDetail({ employeeCode }).then((res: any) => {
            const detail = res?.result || res || null;
            this.setState({ detail, showLoader: false });
        });
    }

    handleStatusChange = (row: any, newStatus: string) => {
        const { employeeCode } = this.state;

        if (newStatus === 'Active') {
            this.setState({ showLoader: true });
            getKpiReport({ effectiveFrom: row.startDate, employeeCode, reportType: 'employee' }).then((res: any) => {
                const report = res?.result || null;
                if (!report || report.kpi_status !== 'rvm_approved') {
                    alert('KPI report must be submitted by the employee and fully approved by both managers before activating this period.');
                    this.setState({ showLoader: false });
                    return;
                }
                this.updateStatus(row, newStatus);
            });
        } else {
            this.updateStatus(row, newStatus);
        }
    };

    updateStatus = (row: any, newStatus: string) => {
        const { employeeCode } = this.state;
        this.setState({ showLoader: true });

        updatePeriodStatus({
            appraisalCycleId: this.state.detail?.cycle?.id,
            employeeCode,
            effectiveFrom: row.startDate,
            status: newStatus,
        }).then((res: any) => {
            if (res?.result || res?.success) {
                const detail = { ...this.state.detail };
                if (row.isOriginal) {
                    detail.status = newStatus;
                } else {
                    const transferList = [...(detail.transferList || [])];
                    const tIdx = parseInt(row.key.split('-')[1], 10);
                    if (transferList[tIdx]) {
                        transferList[tIdx] = { ...transferList[tIdx], status: newStatus };
                    }
                    detail.transferList = transferList;
                }
                this.setState({ detail, showLoader: false });
            } else {
                this.setState({ showLoader: false });
            }
        });
    };

    selectPeriod = (row: any) => {
        const { employeeCode, type, detail } = this.state;

        // For APAR Report type, navigate directly to the full consolidated report
        if (type === 'apar') {
            this.props.history.push({
                pathname: CONSTANT.component.hrAparFullReport.url,
                state: {
                    employeeCode,
                    appraisalCycleId: detail?.cycle?.id,
                    periodId: row.periodId,
                },
            });
            return;
        }

        const selectedRecord = { ...row, cycle: detail?.cycle };
        const isMouApplicable = detail?.cycle?.is_mou_applicable || false;

        this.setState({ showLoader: true });

        getKpiReport({ effectiveFrom: row.startDate, employeeCode, reportType: 'employee' }).then((res: any) => {
            const report = res?.result || null;

            if (!report) {
                this.setState({ showLoader: false });
                alert('KPI report has not been submitted by the employee yet.');
                return;
            }

            const kpiReportId = report?.id || null;
            const mouTask = report?.tasks?.find((t: any) => t.particular === '*)') || null;
            const otherTasks = report?.tasks
                ?.filter((t: any) => t.particular !== '*)')
                .map((t: any) => ({
                    id:              t.id,
                    particular:      t.particular,
                    taskDescription: t.task_description || '',
                    weightage:       t.weightage        || '',
                    deliverables:    t.deliverables     || '',
                    achievement:     t.achievement      || '',
                }));

            this.setState({ showLoader: false });
            this.props.history.push({
                pathname: CONSTANT.component.hrReportDetail.url,
                search: `?type=${type}&employeeCode=${employeeCode}`,
                state: {
                    selectedRecord,
                    employeeCode,
                    employeeNumber: detail?.employeeNumber,
                    type,
                    kpiReportId,
                    isMouApplicable,
                    mouTaskId:          mouTask?.id               || null,
                    mouTaskDescription: mouTask?.task_description || '',
                    mouWeightage:       mouTask?.weightage        || '',
                    mouDeliverables:    mouTask?.deliverables     || '',
                    mouAchievement:     mouTask?.achievement      || '',
                    otherKeyTasks: otherTasks?.length > 0 ? otherTasks : DEFAULT_TASKS.map(t => ({ ...t })),
                },
            });
        });
    };

    render() {
        const { showLoader, type, employeeCode, detail } = this.state;
        const typeInfo = TYPE_LABELS[type] || {};

        const rows: any[] = [];
        if (detail) {
            const cycle         = detail.cycle || {};
            const cycleName     = cycle.cycle_name || cycle.financial_year || `Cycle ${cycle.id}`;
            const financialYear = cycle.financial_year || '-';

            rows.push({
                key: `orig-${cycle.id}`, cycleName, financialYear,
                periodId:   detail.periodId,
                startDate:  detail.startDate,
                endDate:    detail.endDate,
                periodType: 'Original', isOriginal: true, cycleId: cycle.id,
                periodStatus: detail.status || 'Draft',
                reportingManagerName: detail.reportingManagerName || null,
                reviewingManagerName: detail.reviewingManagerName || null,
                acceptingAuthorityName: detail.acceptingAuthorityName || null,
            });

            (detail.transferList || []).forEach((t: any, idx: number) => {
                rows.push({
                    key: `transfer-${idx}`, cycleName, financialYear,
                    periodId:   t.periodId,
                    startDate:  t.startDate,
                    endDate:    t.endDate,
                    periodType: `Period #${idx + 1}`, isOriginal: false, cycleId: cycle.id,
                    periodStatus: t.status || 'Draft',
                    reportingManagerName: t.reportingManagerName || null,
                    reviewingManagerName: t.reviewingManagerName || null,
                    acceptingAuthorityName: t.acceptingAuthorityName || null,
                });
            });
        }

        return (
            <div className="container-fluid mb-3">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card homedob">
                            <div className="row">
                                <div className="col-md-8">
                                    <h1 className="page-title">
                                        <i className={`fa ${typeInfo.icon} ${typeInfo.color} mr-2`} />
                                        {typeInfo.label} — Appraisal Periods
                                    </h1>
                                    <p className="text-muted mb-0">Employee Number: <strong>{detail?.employeeNumber}</strong></p>
                                </div>
                                <div className="col-md-4 text-right">
                                    <button className="btn btn-sm cancel-button" onClick={() => this.props.history.goBack()}>
                                        <i className="fa fa-arrow-left mr-2" />Back
                                    </button>
                                </div>
                            </div>

                            {showLoader && <Loader />}

                            <div className="row mt-3">
                                <div className="col-md-12">
                                    {!showLoader && rows.length === 0 ? (
                                        <div className="alert alert-warning">No appraisal periods found for this employee.</div>
                                    ) : (
                                        <div className="table-container">
                                            <div className="table-wrapper radius-4">
                                                <table className="tablen">
                                                    <thead>
                                                        <tr className="text-center">
                                                            <th className="table-dark">#</th>
                                                            <th className="table-dark">Cycle</th>
                                                            <th className="table-dark">Financial Year</th>
                                                            <th className="table-dark">Period</th>
                                                            {/* <th className="table-dark">Type</th> */}
                                                            <th className="table-dark">Reporting Manager</th>
                                                            <th className="table-dark">Reviewing Manager</th>
                                                            <th className="table-dark">Accepting Authority</th>
                                                            {type === 'hr' && <th className="table-dark">Status</th>}
                                                            <th className="table-dark">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {rows.map((row: any, idx: number) => (
                                                            <tr key={row.key} className="text-center">
                                                                <td>{idx + 1}</td>
                                                                <td>{row.cycleName}</td>
                                                                <td>{row.financialYear}</td>
                                                                <td>
                                                                    {formatDateDDMMYY(row.startDate)}
                                                                    {row.endDate ? ` to ${formatDateDDMMYY(row.endDate)}` : ' onwards'}
                                                                </td>
                                                                {/* <td>
                                                                    <span className={`badge ${row.isOriginal ? 'badge-success' : 'badge-warning'}`}>
                                                                        {row.periodType}
                                                                    </span>
                                                                </td> */}
                                                                <td>{row.reportingManagerName || <span className="text-danger small">Not Assigned</span>}</td>
                                                                <td>{row.reviewingManagerName || <span className="text-danger small">Not Assigned</span>}</td>
                                                                <td>{row.acceptingAuthorityName || <span className="text-danger small">Not Assigned</span>}</td>
                                                                {type === 'hr' && (
                                                                    <td>
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={row.periodStatus}
                                                                            onChange={(e) => this.handleStatusChange(row, e.target.value)}
                                                                        >
                                                                            <option value="Draft">Draft</option>
                                                                            <option value="Active">Active</option>
                                                                            <option value="Completed">Completed</option>
                                                                        </select>
                                                                    </td>
                                                                )}
                                                                <td>
                                                                    <button className="btn btn-sm submit-button" onClick={() => this.selectPeriod(row)}>
                                                                        <i className="fa fa-eye mr-1" />
                                                                        View Report
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(HrReportDetailList);
